import {
  Alert,
  Button,
  classNames,
  InputError,
  ScreenReaderOnly,
  Table,
  TableBody,
  TableDataCell,
  TableHead,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from '@op/opds.opux';
import { IconAdd, IconEdit } from '@op/opds.opux-icons/action';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import styles from './BeneficiarySelect.module.scss';
import NamedBeneficiariesModal from './NamedBeneficiaryModal';
import {
  NamedBeneficiaryType,
  type NamedCompanyOrCommunityBeneficiary,
  type NamedPersonBeneficiary,
} from 'dto/beneficiary';
import i18n from 'i18n';
import { selectNamedBeneficiariesTotalPercentage } from 'state/selectors/beneficiarySelectors';
import { useAppStore } from 'state/store';
import { getNamedBeneficiaryIdentifier } from 'utils';

interface NamedBeneficiariesListProps {
  setEditableBeneficiary: (params: {
    index: number;
    value: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary;
  }) => void;
  setOpenModal: (value: boolean) => void;
}

const NamedBeneficiariesList = ({
  setEditableBeneficiary,
  setOpenModal,
}: NamedBeneficiariesListProps) => {
  const getBeneficiaryKey = (
    beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  ) => {
    if (beneficiary.namedBeneficiaryType === NamedBeneficiaryType.PERSON) {
      return (beneficiary as NamedPersonBeneficiary).ssn;
    } else if (
      beneficiary.namedBeneficiaryType ===
      NamedBeneficiaryType.COMPANY_OR_COMMUNITY
    ) {
      return (beneficiary as NamedCompanyOrCommunityBeneficiary).businessId;
    }
  };

  const namedBeneficiaries = useAppStore.use.namedBeneficiaries();

  const editBeneficiary = (
    event: React.MouseEvent<any> | React.KeyboardEvent<any>,
    index: number,
    beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  ): void => {
    event.preventDefault();
    setEditableBeneficiary({ index, value: beneficiary });
    setOpenModal(true);
  };

  return (
    <>
      {namedBeneficiaries.map((beneficiary, index: number) => (
        <TableRow
          data-testid={`named-beneficiary-row-${getNamedBeneficiaryIdentifier(beneficiary)}`}
          key={getBeneficiaryKey(beneficiary)}
          className={[styles.verticalSpace, styles.beneficiaryList].join(' ')}>
          <TableDataCell>
            {'ssn' in beneficiary && (
              <>
                <p>
                  {beneficiary.firstName} {beneficiary.lastName}{' '}
                  {beneficiary.maritalRightsExcluded && (
                    <>
                      *{' '}
                      <ScreenReaderOnly>
                        {i18n.t('beneficiarySelect.maritalRightsExclusion')}
                      </ScreenReaderOnly>
                    </>
                  )}
                </p>
                <p className={styles.secondary}>{beneficiary.ssn}</p>
              </>
            )}
            {'businessId' in beneficiary && (
              <>
                <p className={styles.wordBreakAll}>{beneficiary.name}</p>
                <p className={styles.secondary}>{beneficiary.businessId}</p>
              </>
            )}
          </TableDataCell>
          <td className={styles.percentageCell}>{beneficiary.percentage} %</td>
          <td className={styles.editCell}>
            <Button
              className={styles.editButton}
              icon={IconEdit}
              iconLabel={`${i18n.t('beneficiarySelect.editBeneficiary')} ${
                'ssn' in beneficiary
                  ? `${beneficiary.firstName} ${beneficiary.lastName}`
                  : `${beneficiary.name}`
              }`}
              ghost
              type='button'
              data-testid={`edit-beneficiary-button-${getNamedBeneficiaryIdentifier(beneficiary)}`}
              onClick={(event) => {
                editBeneficiary(event, index, beneficiary);
              }}
            />
          </td>
        </TableRow>
      ))}
    </>
  );
};

const AddNamedBeneficiaryButton = ({
  disabled,
  onClick,
  showError,
  errorId,
  ...props
}: {
  disabled: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  showError: boolean;
  errorId: string;
  [key: string]: any;
}) => {
  return (
    <Button
      tabIndex={0}
      compact
      icon={IconAdd}
      disabled={disabled}
      onClick={onClick}
      id='namedBeneficiaries'
      data-testid='add-named-beneficiary-button'
      aria-describedby={showError ? errorId : undefined}
      {...props}>
      {i18n.t('beneficiarySelect.addNamedBeneficiary')}
    </Button>
  );
};

export default function NamedBeneficiaries() {
  const [openModal, setOpenModal] = useState(false);
  const [editableBeneficiary, setEditableBeneficiary] = useState<{
    index: number;
    value: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary;
  }>();

  const [deletedBeneficiary, setDeletedBeneficiary] = useState<
    NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary | null
  >(null);

  const [addedBeneficiary, setAddedBeneficiary] = useState<
    NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary | null
  >(null);

  const [editedBeneficiary, setEditedBeneficiary] = useState<
    NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary | null
  >(null);
  const [renderAlert, setRenderAlert] = useState(false);

  const setNamedBeneficiaries = useAppStore(
    (state) => state.setNamedBeneficiaries
  );
  const { namedBeneficiaries, errors } = useAppStore(
    useShallow((state) => ({
      namedBeneficiaries: state.namedBeneficiaries,
      errors: state.errors,
    }))
  );
  const totalPercentage =
    useAppStore(selectNamedBeneficiariesTotalPercentage) ?? 0;

  const addBeneficiary = (
    beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  ): void => {
    setNamedBeneficiaries([...namedBeneficiaries, beneficiary]);
    setAddedBeneficiary(beneficiary);
  };

  const editBeneficiary = (
    index: number,
    beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  ): void => {
    const mutated = customSplice(namedBeneficiaries, index, 1, beneficiary);
    setNamedBeneficiaries(mutated);
    setEditedBeneficiary(beneficiary);
  };

  const deleteBeneficiary = (): void => {
    if (editableBeneficiary) {
      const mutated = customSplice(
        namedBeneficiaries,
        editableBeneficiary?.index,
        1,
        undefined
      );
      setNamedBeneficiaries(mutated);
      setOpenModal(false);
      setDeletedBeneficiary(editableBeneficiary.value);
    }
  };

  // Returns a spliced deep copy of the input array without mutating the original
  const customSplice = (
    arr: any[],
    start: number,
    deleteCount: number,
    ...addItem: any
  ) => {
    const result = [];
    if (start > 0) {
      result.push(...arr.slice(0, start));
    }
    let len;
    if (addItem[0]) {
      result.push(...addItem);
      len = result.length - addItem.length;
    } else len = result.length;
    const count = deleteCount <= 0 ? len : len + deleteCount;
    if (arr[count]) {
      result.push(...arr.slice(count));
    }
    return result;
  };

  const anyHaveMaritalRightsExclusion = (): boolean | undefined => {
    let res: boolean | undefined;
    namedBeneficiaries.forEach((beneficiary) => {
      if (
        beneficiary.namedBeneficiaryType === NamedBeneficiaryType.PERSON &&
        'maritalRightsExcluded' in beneficiary &&
        beneficiary.maritalRightsExcluded
      )
        res = true;
    });
    return res;
  };

  const handleHideAlert = () => {
    setTimeout(() => {
      setDeletedBeneficiary(null);
      setAddedBeneficiary(null);
      setEditedBeneficiary(null);

      setRenderAlert(false);
    }, 500);
  };

  const showError =
    (namedBeneficiaries.length !== 0 && totalPercentage !== 100) ||
    (!!errors?.namedBeneficiaries && namedBeneficiaries.length === 0);

  const namedBeneficiariesErrorId = 'namedBeneficiariesErrorId';

  const handleCloseModal = () => {
    setRenderAlert(true);
  };

  return (
    <div className={styles.namedContainer}>
      <p className={styles.bolder}>
        {i18n.t('beneficiarySelect.NAMED_BENEFICIARY_info2')}
      </p>
      {namedBeneficiaries.length ? (
        <Table style={{ width: '100%' }} layout='tile'>
          <TableHead>
            <ScreenReaderOnly>
              <TableHeaderRow>
                <TableHeaderCell>
                  {i18n.t('beneficiarySelect.beneficiary')}
                </TableHeaderCell>
                <TableHeaderCell style={{ textAlign: 'right' }}>
                  {i18n.t('beneficiarySelect.percentage')}
                </TableHeaderCell>
                <TableHeaderCell align='center' scope='col'>
                  {i18n.t('beneficiarySelect.editBeneficiary')}
                </TableHeaderCell>
              </TableHeaderRow>
            </ScreenReaderOnly>
          </TableHead>
          <TableBody className='opds-styles-v1-text-body-medium'>
            <NamedBeneficiariesList
              setEditableBeneficiary={setEditableBeneficiary}
              setOpenModal={setOpenModal}
            />
            <TableRow>
              <TableDataCell>
                <AddNamedBeneficiaryButton
                  disabled={namedBeneficiaries.length > 14}
                  onClick={(event) => {
                    event.preventDefault();
                    setOpenModal(true);
                  }}
                  showError={showError}
                  errorId={namedBeneficiariesErrorId}
                />
              </TableDataCell>
            </TableRow>
            {namedBeneficiaries.length !== 0 && (
              <>
                <TableRow
                  className={[
                    styles.semiBorderedBottom,
                    styles.beneficiaryList,
                    totalPercentage !== 100 ? styles.error : '',
                  ].join(' ')}>
                  <TableDataCell className={styles.borderTop}>
                    <p>{i18n.t('beneficiarySelect.totalPercentage')}</p>
                  </TableDataCell>
                  <TableDataCell
                    className={[styles.percentageCell, styles.borderTop].join(
                      ' '
                    )}
                    data-testid='totalPercentage'>
                    {totalPercentage} %
                  </TableDataCell>
                  <TableDataCell className={styles.borderTop}></TableDataCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      ) : (
        <div
          className={classNames(
            styles.semiBordered,
            styles.noNamedBeneficiariesWrapper
          )}>
          <p>{i18n.t('beneficiarySelect.noNamedBeneficiaries')}</p>
          <AddNamedBeneficiaryButton
            disabled={false}
            onClick={(event) => {
              event.preventDefault();
              setOpenModal(true);
            }}
            showError={showError}
            errorId={namedBeneficiariesErrorId}
          />
        </div>
      )}
      {showError && (
        <div className={styles.semiBorderedBottom} tabIndex={-1}>
          <InputError
            error={
              namedBeneficiaries.length === 0
                ? i18n.t('beneficiarySelect.common.required')
                : i18n.t('beneficiarySelect.totalPercentageError')
            }
            id={namedBeneficiariesErrorId}
          />
        </div>
      )}
      {anyHaveMaritalRightsExclusion() && (
        <p data-testid='marital-rights-exclusion-explanatory-text'>
          *) {i18n.t('beneficiarySelect.maritalRightsExclusion')}
        </p>
      )}
      <NamedBeneficiariesModal
        open={openModal}
        setOpen={setOpenModal}
        addBeneficiary={addBeneficiary}
        editableBeneficiary={editableBeneficiary}
        setEditable={setEditableBeneficiary}
        edit={editBeneficiary}
        delete={deleteBeneficiary}
        handleCloseModal={handleCloseModal}
      />
      <div role='status'>
        {renderAlert && (
          <ScreenReaderOnly>
            {deletedBeneficiary && (
              <Alert
                data-testid='deleted-beneficiary-alert'
                tabIndex={-1}
                flash
                onHide={handleHideAlert}>
                {'ssn' in deletedBeneficiary
                  ? `${deletedBeneficiary.firstName} ${deletedBeneficiary.lastName}`
                  : `${deletedBeneficiary.name}`}{' '}
                {i18n.t('beneficiarySelect.beneficiaryDeleted')}
              </Alert>
            )}
            {addedBeneficiary && (
              <Alert
                data-testid='added-beneficiary-alert'
                tabIndex={-1}
                flash
                onHide={handleHideAlert}>
                {'ssn' in addedBeneficiary
                  ? `${addedBeneficiary.firstName} ${addedBeneficiary.lastName}`
                  : `${addedBeneficiary.name}`}{' '}
                {i18n.t('beneficiarySelect.beneficiaryAdded')}
              </Alert>
            )}
            {editedBeneficiary && (
              <Alert
                data-testid='edited-beneficiary-alert'
                tabIndex={-1}
                flash
                onHide={handleHideAlert}>
                {'ssn' in editedBeneficiary
                  ? `${editedBeneficiary.firstName} ${editedBeneficiary.lastName}`
                  : `${editedBeneficiary.name}`}{' '}
                {i18n.t('beneficiarySelect.beneficiaryEdited')}
              </Alert>
            )}
          </ScreenReaderOnly>
        )}
      </div>
    </div>
  );
}
